/* CUSTOM studio-calendar.js — gemeinsamer Kalender (Praesenz + Buchung).
 * Horizontaler Tages-Streifen (Felder mit Uhrzeiten beschriftet), 2-Wochen-Fenster
 * ab Montag dieser Woche, Blaettern vorwaerts, heute hervorgehoben. Liest Google
 * Calendar (read-only API-Key).
 * mode="display": nur Anzeige (Status + Zeiten).
 * mode="booking": open/at-studio-Tage klickbar -> freie Slots darunter -> Name/Mail
 *   -> Shopify-Kontaktformular-Mail (oder optionaler JSON-Endpoint).
 */
(function () {
  if (customElements.get('studio-calendar')) return;
  const DAY = 86400000;
  const LOCALE = 'en-GB';

  class StudioCalendar extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'display';
      this.calendarId = this.dataset.calendarId || '';
      this.apiKey = this.dataset.apiKey || '';
      this.tz = this.dataset.tz || 'Europe/Berlin';
      this.windowDays = parseInt(this.dataset.windowDays, 10) || 14;
      this.slotMin = parseInt(this.dataset.slotMin, 10) || 30;
      this.slotMs = this.slotMin * 60000;
      this.earliestHour = parseInt(this.dataset.earliestHour, 10);
      if (isNaN(this.earliestHour)) this.earliestHour = 10;
      this.bookable = (this.dataset.bookable || 'at the studio,open studio')
        .split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      this.endpoint = this.dataset.endpoint || '';
      this.successMsg = this.dataset.success || 'Thanks, your visit is booked. Check your inbox for the calendar invite.';

      this.rangeEl = this.querySelector('[data-range]');
      this.listEl = this.querySelector('[data-list]');
      this.hintEl = this.querySelector('[data-hint]');
      this.liveEl = this.querySelector('[data-live]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.slotPanel = this.querySelector('[data-slotpanel]');
      this.reqBox = this.querySelector('[data-request]');
      this.futureBox = this.querySelector('[data-future]');
      this.futurePick = this.querySelector('[data-future-pick]');
      this.futureDate = this.querySelector('[data-future-date]');
      this.formEl = this.querySelector('.studio-cal__form');
      this.formBody = this.querySelector('[data-form-body]');
      this.fBooking = this.querySelector('[data-f-booking]');
      this.fStart = this.querySelector('[data-f-start]');
      this.fEnd = this.querySelector('[data-f-end]');
      this.fWhen = this.querySelector('[data-f-when]');
      this.chosenEl = this.querySelector('[data-chosen]');
      this.topicEl = this.querySelector('[data-topic]');
      this.nameEl = this.querySelector('[data-name]');
      this.emailEl = this.querySelector('[data-email]');
      this.phoneEl = this.querySelector('[data-phone]');
      this.noteEl = this.querySelector('[data-note]');
      this.submitEl = this.querySelector('[data-submit]');
      this.feedbackEl = this.querySelector('[data-feedback]');

      this.offset = 0;
      this.selected = null;
      this.futureMode = false;
      this.futureVal = null;
      this.cache = new Map();

      if (!this.calendarId || !this.apiKey || this.apiKey === 'ENTER_API_KEY') {
        if (this.listEl) this.listEl.innerHTML = '<p class="studio-cal__status">Please set Calendar ID and API key in the section settings.</p>';
        return;
      }

      const now = new Date();
      const dow = now.getDay();
      const toMon = dow === 0 ? -6 : 1 - dow;
      this.baseMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + toMon);

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => { if (this.offset > 0) { this.offset--; this.render(); } });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.offset++; this.render(); });
      if (this.formEl) this.formEl.addEventListener('submit', (e) => this.onSubmit(e));
      if (this.reqBox) {
        const open = () => this.openRequest();
        this.reqBox.addEventListener('click', open);
        this.reqBox.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
      }
      if (this.futureBox) {
        const openF = () => this.openFuture();
        this.futureBox.addEventListener('click', openF);
        this.futureBox.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openF(); } });
      }
      if (this.futureDate) this.futureDate.addEventListener('change', () => this.onFutureDate());

      // Nach nativem Kontaktformular-Submit: Erfolg anzeigen
      if (new URLSearchParams(window.location.search).get('contact_posted') === 'true') {
        this.querySelectorAll('.studio-cal__topbox, .studio-legend, .studio-cal__reqbox, .studio-cal__hint').forEach((el) => { el.style.display = 'none'; });
        if (this.listEl) this.listEl.style.display = 'none';
        this.announce(this.successMsg);
        return;
      }

      this.render();
    }

    // ── Helpers ─────────────────────────────────────────
    fmt(d, opts) { return d.toLocaleString(LOCALE, { ...opts, timeZone: this.tz }); }
    fmtTime(d) { return this.fmt(d, { hour: '2-digit', minute: '2-digit' }); }
    // Lesbarer Zeitstempel in Studio-Zeitzone (kein UTC-„Z" in der Mail).
    fmtStamp(d) { return `${d.toLocaleString('sv-SE', { timeZone: this.tz })} (${this.tz})`; }
    fmtDow(d) { return this.fmt(d, { weekday: 'short' }); }
    fmtNum(d) { return this.fmt(d, { day: 'numeric' }); }
    fmtDayLong(d) { return this.fmt(d, { weekday: 'long', day: 'numeric', month: 'long' }); }
    dateKey(d) { return d.toLocaleDateString('sv-SE', { timeZone: this.tz }); }

    normalizeStatus(raw) {
      const s = (raw || '').trim().toLowerCase();
      if (['studio', 'studio wip', 'at the studio'].includes(s)) return 'studio wip';
      if (['workshop', 'workshop ongoing'].includes(s)) return 'workshop';
      if (s === 'open studio') return 'open studio';
      if (s === 'studio sale') return 'studio sale';
      if (s === 'event') return 'event';
      return null;
    }
    badgeClass(s) { return 'badge badge-' + s.replace(/\s+/g, '-'); }

    windowStart() { return new Date(this.baseMonday.getTime() + this.offset * this.windowDays * DAY); }

    async fetchEvents(start, end) {
      const key = this.dateKey(start);
      if (this.cache.has(key)) return this.cache.get(key);
      const url = new URL(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(this.calendarId)}/events`);
      url.searchParams.set('key', this.apiKey);
      url.searchParams.set('singleEvents', 'true');
      url.searchParams.set('orderBy', 'startTime');
      url.searchParams.set('timeMin', start.toISOString());
      url.searchParams.set('timeMax', end.toISOString());
      const resp = await fetch(url.toString());
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error?.message || resp.statusText);
      const items = (data.items || [])
        .filter((ev) => ev.start && (ev.start.dateTime || ev.start.date))
        .map((ev) => ({ startD: new Date(ev.start.dateTime || ev.start.date), endD: new Date(ev.end.dateTime || ev.end.date), status: this.normalizeStatus(ev.summary) }));
      this.cache.set(key, items);
      return items;
    }

    async render() {
      const start = this.windowStart();
      const end = new Date(start.getTime() + this.windowDays * DAY);
      if (this.prevBtn) this.prevBtn.disabled = this.offset === 0;
      if (this.rangeEl) {
        try {
          this.rangeEl.textContent = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone: this.tz }).formatRange(start, new Date(end.getTime() - DAY));
        } catch { this.rangeEl.textContent = `${this.fmtNum(start)} – ${this.fmtNum(new Date(end.getTime() - DAY))}`; }
      }
      this.listEl.innerHTML = '<p class="studio-cal__status">Loading…</p>';
      if (this.slotPanel) this.slotPanel.innerHTML = '';
      if (this.formBody) this.formBody.hidden = true;
      this.selected = null;
      this.requestMode = false;
      this.resetFuture();
      this.classList.remove('is-picking');
      if (this.reqBox) this.reqBox.classList.remove('is-active');
      try {
        const events = await this.fetchEvents(start, end);
        this.renderStrip(events.filter((e) => e.status), start);
        this.renderHint();
      } catch (err) {
        this.listEl.innerHTML = `<p class="studio-cal__status">Could not load: ${err?.message || 'error'}</p>`;
        console.error('Calendar error:', err);
      }
    }

    renderHint() {
      if (!this.hintEl) return;
      const weeks = Math.round(this.windowDays / 7);
      const nextReveal = new Date(this.baseMonday.getTime() + 7 * DAY);
      const nextWeekStart = new Date(this.baseMonday.getTime() + this.windowDays * DAY);
      const short = (d) => new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', timeZone: this.tz }).format(d);
      this.hintEl.textContent = `Showing the next ${weeks} weeks; the week of ${short(nextWeekStart)} opens ${this.fmt(nextReveal, { weekday: 'long', day: 'numeric', month: 'short' })}.`;
    }

    // ── Horizontaler Tages-Streifen ─────────────────────
    renderStrip(events, start) {
      const now = new Date();
      const todayKey = this.dateKey(now);
      const byDay = new Map();
      for (const ev of events) {
        const k = this.dateKey(ev.startD);
        if (!byDay.has(k)) byDay.set(k, []);
        byDay.get(k).push(ev);
      }
      byDay.forEach((arr) => arr.sort((a, b) => a.startD - b.startD));

      this.dayCache = new Map();
      const booking = this.mode === 'booking';
      const cards = [];

      for (let i = 0; i < this.windowDays; i++) {
        const day = new Date(start.getTime() + i * DAY);
        const key = this.dateKey(day);
        const isToday = key === todayKey;
        const isPast = !isToday && key < todayKey;
        const evs = byDay.get(key) || [];
        const slots = booking ? this.slotsForDay(evs, evs, now) : [];
        if (booking) this.dayCache.set(key, { day, slots });
        const canBook = booking && slots.length > 0;

        // Buchungskalender: Workshops komplett ausblenden (blockieren Slots weiterhin via slotsForDay).
        const evsDisplay = booking ? evs.filter((e) => !/workshop/i.test(e.status)) : evs;
        const empty = !evsDisplay.length;
        const inner = empty ? '' : evsDisplay.map((ev) => {
          let act = '', actMod = '';
          if (booking) {
            if (this.bookable.includes(ev.status)) {
              if (/open/i.test(ev.status)) { act = 'book or drop by'; actMod = 'is-book'; }
              else { act = 'request a time'; actMod = 'is-request'; }
            } else if (/sale/i.test(ev.status)) {
              act = 'open · drop by'; actMod = 'is-dropby';
            }
          }
          const actHtml = act ? `<span class="studio-cal__c-act ${actMod}">${act}</span>` : '';
          return `<span class="studio-cal__c-event"><span class="studio-cal__c-time">${this.fmtTime(ev.startD)}–${this.fmtTime(ev.endD)}</span><span class="${this.badgeClass(ev.status)}">${ev.status}</span>${actHtml}</span>`;
        }).join('');

        const cls = ['studio-cal__card', isToday ? 'is-today' : '', isPast ? 'is-past' : '', empty ? 'is-empty' : '', canBook ? 'is-bookable' : ''].filter(Boolean).join(' ');
        const head = `<span class="studio-cal__c-dow">${this.fmtDow(day)}</span><span class="studio-cal__c-num">${this.fmtNum(day)}</span>`;
        const body = `<span class="studio-cal__c-events">${inner}</span>`;
        if (canBook) {
          cards.push(`<button type="button" class="${cls}" data-key="${key}" aria-pressed="false" aria-label="Show free times on ${this.fmt(day, { weekday: 'long', day: 'numeric', month: 'long' })}">${head}${body}</button>`);
        } else {
          cards.push(`<div class="${cls}">${head}${body}</div>`);
        }
      }

      let weeksHtml = '';
      for (let w = 0; w < cards.length; w += 7) {
        weeksHtml += `<div class="studio-cal__week">${cards.slice(w, w + 7).join('')}</div>`;
      }
      this.listEl.innerHTML = `<div class="studio-cal__weeks">${weeksHtml}</div>`;

      if (booking) {
        this.listEl.querySelectorAll('.studio-cal__card.is-bookable').forEach((card) => {
          card.addEventListener('click', () => this.showDaySlots(card));
        });
      }
    }

    // ── Buchung ─────────────────────────────────────────
    slotsForDay(dayEvents, allEventsToday, now) {
      const windows = dayEvents.filter((e) => this.bookable.includes(e.status));
      const overlapsBusy = (s, e) => allEventsToday.some((b) => !this.bookable.includes(b.status) && b.startD < e && b.endD > s);
      // Keine Slots vor der fruehesten Buchungsstunde (Studio-Zeitzone). Default 10:00.
      const hourInTz = (d) => parseInt(d.toLocaleString('en-GB', { timeZone: this.tz, hour: '2-digit', hour12: false }), 10);
      const slots = [];
      for (const w of windows) {
        let s = new Date(w.startD);
        while (s.getTime() + this.slotMs <= w.endD.getTime()) {
          const e = new Date(s.getTime() + this.slotMs);
          if (s > now && hourInTz(s) >= this.earliestHour && !overlapsBusy(s, e)) slots.push({ start: new Date(s), end: e });
          s = e;
        }
      }
      slots.sort((a, b) => a.start - b.start);
      return slots;
    }

    showDaySlots(card) {
      // Erneuter Klick auf denselben Tag -> abwaehlen (Legende kommt zurueck)
      if (card.classList.contains('is-active')) {
        card.classList.remove('is-active');
        card.setAttribute('aria-pressed', 'false');
        this.classList.remove('is-picking');
        if (this.slotPanel) this.slotPanel.innerHTML = '';
        if (this.formBody) this.formBody.hidden = true;
        this.selected = null;
        if (this.submitEl) this.submitEl.textContent = 'Request a visit';
        this.announce('Day deselected.');
        return;
      }
      this.requestMode = false;
      this.resetFuture();
      this.classList.add('is-picking');
      if (this.reqBox) this.reqBox.classList.remove('is-active');
      if (this.submitEl) this.submitEl.textContent = 'Request a visit';
      this.listEl.querySelectorAll('.studio-cal__card.is-active').forEach((c) => c.classList.remove('is-active'));
      this.listEl.querySelectorAll('.studio-cal__card[aria-pressed]').forEach((c) => c.setAttribute('aria-pressed', 'false'));
      card.classList.add('is-active');
      card.setAttribute('aria-pressed', 'true');
      if (this.formBody) this.formBody.hidden = true;
      this.selected = null;

      const entry = this.dayCache.get(card.dataset.key);
      const slots = entry ? entry.slots : [];
      if (!this.slotPanel) return;
      let html = `<p class="studio-cal__slots-head">${this.fmtDayLong(entry.day)}</p>`;
      if (!slots.length) {
        html += `<p class="studio-cal__status">No free slots that day.</p>`;
        this.slotPanel.innerHTML = html;
        this.announce(`No free times on ${this.fmtDayLong(entry.day)}`);
        return;
      }
      // Zusammenhaengende Laeufe der 30-Min-Slots -> Grenz-Zeitpunkte fuer den Von/Bis-Picker.
      const runs = [];
      let cur = null;
      slots.forEach((sl) => {
        if (cur && +cur[cur.length - 1] === +sl.start) cur.push(sl.end);
        else { cur = [sl.start, sl.end]; runs.push(cur); }
      });
      this.rangeRuns = runs;
      let fromOpts = '';
      runs.forEach((run, ri) => {
        for (let bi = 0; bi < run.length - 1; bi++) {
          fromOpts += `<option value="${ri}|${bi}">${this.fmtTime(run[bi])}</option>`;
        }
      });
      this.slotPanel.innerHTML = `${html}
        <p class="studio-cal__range-hint">Tell me the window you're free; I'll confirm a 30-minute time within it.</p>
        <div class="studio-cal__rangepick">
          <label class="studio-cal__rangefield"><span class="studio-cal__rangelabel">From</span>
            <select class="studio-cal__rangeselect" data-range-from aria-label="Available from"><option value="" selected disabled>—</option>${fromOpts}</select>
          </label>
          <label class="studio-cal__rangefield"><span class="studio-cal__rangelabel">to</span>
            <select class="studio-cal__rangeselect" data-range-to disabled aria-label="Available to"><option value="" selected disabled>—</option></select>
          </label>
        </div>`;
      const fromSel = this.slotPanel.querySelector('[data-range-from]');
      const toSel = this.slotPanel.querySelector('[data-range-to]');
      fromSel.addEventListener('change', () => this.onRangeFrom(fromSel, toSel));
      toSel.addEventListener('change', () => this.onRangeTo(fromSel, toSel));
      this.announce(`Choose a window you're free on ${this.fmtDayLong(entry.day)}`);
    }

    // Formular einblenden — iOS-sicher: Scroll erst nach dem naechsten Frame + kurzem Delay
    // (damit ein noch schliessender nativer Date-/Select-Picker nicht abgerissen wird) und nur
    // 'nearest' (kein grosser Sprung). KEIN Auto-Focus auf Touch (iOS ignoriert ihn eh und er
    // kann Keyboard/Picker kapern); auf Desktop das Name-Textfeld fokussieren (nie das <select>).
    revealForm() {
      if (!this.formBody) return;
      this.formBody.hidden = false;
      const el = this.formBody, name = this.nameEl;
      const desktop = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      requestAnimationFrame(() => {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          if (desktop && name) { try { name.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
        }, 60);
      });
    }

    selectSlot(sl, btn) {
      this.selected = sl;
      this.querySelectorAll('.studio-cal__slot').forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');
      const label = `${this.fmtDayLong(sl.start)} · ${this.fmtTime(sl.start)}–${this.fmtTime(sl.end)}`;
      if (this.chosenEl) this.chosenEl.textContent = label;
      this.syncBooking();
      this.revealForm();
      this.announce(`Selected ${label}. Add your details below.`);
      if (this.feedbackEl) { this.feedbackEl.textContent = ''; this.feedbackEl.className = 'studio-cal__feedback'; }
    }

    onRangeFrom(fromSel, toSel) {
      const parts = (fromSel.value || '').split('|');
      if (parts.length !== 2) { toSel.innerHTML = '<option value="" selected disabled>—</option>'; toSel.disabled = true; return; }
      const ri = Number(parts[0]), bi = Number(parts[1]);
      const run = this.rangeRuns[ri] || [];
      let opts = '<option value="" selected disabled>—</option>';
      for (let j = bi + 1; j < run.length; j++) opts += `<option value="${ri}|${j}">${this.fmtTime(run[j])}</option>`;
      toSel.innerHTML = opts;
      toSel.disabled = false;
      this.selected = null;
      if (this.formBody) this.formBody.hidden = true;
    }

    onRangeTo(fromSel, toSel) {
      if (!fromSel.value || !toSel.value) return;
      const [fri, fbi] = fromSel.value.split('|').map(Number);
      const tbi = Number(toSel.value.split('|')[1]);
      const run = this.rangeRuns[fri];
      if (!run) return;
      this.selectRange(run[fbi], run[tbi]);
    }

    selectRange(start, end) {
      this.selected = { start: start, end: end };
      const label = `Available ${this.fmtTime(start)}–${this.fmtTime(end)}; add your details below.`;
      if (this.chosenEl) this.chosenEl.textContent = label;
      this.syncBooking();
      this.revealForm();
      this.announce(`Available ${this.fmtTime(start)}–${this.fmtTime(end)}. Add your details below.`);
      if (this.feedbackEl) { this.feedbackEl.textContent = ''; this.feedbackEl.className = 'studio-cal__feedback'; }
    }

    openFuture() {
      this.selected = null;
      this.futureMode = true;
      this.requestMode = false;
      this.futureVal = null;
      this.classList.add('is-picking');
      this.listEl.querySelectorAll('.studio-cal__card.is-active').forEach((c) => { c.classList.remove('is-active'); c.setAttribute('aria-pressed', 'false'); });
      if (this.slotPanel) this.slotPanel.innerHTML = '';
      if (this.futureBox) this.futureBox.classList.add('is-active');
      if (this.futureDate) {
        const min = new Date(this.baseMonday.getTime() + (this.offset + 1) * this.windowDays * DAY);
        this.futureDate.min = this.dateKey(min);
        this.futureDate.value = '';
      }
      if (this.futurePick) this.futurePick.hidden = false;
      if (this.formBody) this.formBody.hidden = true;
      if (this.submitEl) this.submitEl.textContent = 'Request a visit';
      this.syncBooking();
      if (this.futureDate) { try { if (this.futureDate.showPicker) this.futureDate.showPicker(); } catch (e) { /* Nutzer tippt das Feld an */ } }
      this.announce('Pick a date further out and add your details.');
    }

    onFutureDate() {
      const v = this.futureDate ? this.futureDate.value : '';
      if (!v) { this.futureVal = null; if (this.formBody) this.formBody.hidden = true; this.syncBooking(); return; }
      this.futureVal = v;
      this.futureMode = true;
      this.syncBooking();
      const d = new Date(v + 'T12:00:00');
      if (this.chosenEl) this.chosenEl.textContent = `Requested date: ${this.fmtDayLong(d)}; add your details below.`;
      this.revealForm();
      this.announce(`Requested ${this.fmtDayLong(d)}. Add your details below.`);
      if (this.feedbackEl) { this.feedbackEl.textContent = ''; this.feedbackEl.className = 'studio-cal__feedback'; }
    }

    resetFuture() {
      this.futureMode = false;
      this.futureVal = null;
      if (this.futurePick) this.futurePick.hidden = true;
      if (this.futureBox) this.futureBox.classList.remove('is-active');
    }

    openRequest() {
      this.requestMode = true;
      this.selected = null;
      this.classList.add('is-picking');
      this.listEl.querySelectorAll('.studio-cal__card.is-active').forEach((c) => { c.classList.remove('is-active'); c.setAttribute('aria-pressed', 'false'); });
      if (this.slotPanel) this.slotPanel.innerHTML = '';
      if (this.reqBox) this.reqBox.classList.add('is-active');
      if (this.submitEl) this.submitEl.textContent = 'Send message';
      if (this.chosenEl) this.chosenEl.textContent = "Send a message; I'll find a time with you.";
      this.syncBooking();
      this.revealForm();
      this.announce('Message form open. Add your details.');
      if (this.feedbackEl) { this.feedbackEl.textContent = ''; this.feedbackEl.className = 'studio-cal__feedback'; }
    }

    syncBooking() {
      const hasSlot = !!this.selected;
      const future = this.futureMode && this.futureVal;
      const futureLong = future ? this.fmtDayLong(new Date(this.futureVal + 'T12:00:00')) : '';
      if (this.fBooking) this.fBooking.value = hasSlot ? 'STUDIO VISIT · AVAILABILITY WINDOW' : (future ? 'STUDIO VISIT · FUTURE DATE REQUEST' : 'STUDIO VISIT REQUEST (flexible time)');
      if (this.fStart) this.fStart.value = hasSlot ? this.fmtStamp(this.selected.start) : (future ? `${this.futureVal} (${this.tz})` : '');
      if (this.fEnd) this.fEnd.value = hasSlot ? this.fmtStamp(this.selected.end) : '';
      if (this.fWhen) this.fWhen.value = hasSlot
        ? `${this.fmtDayLong(this.selected.start)} · available ${this.fmtTime(this.selected.start)}–${this.fmtTime(this.selected.end)} (${this.tz})`
        : (future ? `${futureLong} · no specific time yet (${this.tz})` : 'Flexible · no specific slot');
    }

    async onSubmit(e) {
      if (!this.selected && !this.requestMode && !(this.futureMode && this.futureVal)) { e.preventDefault(); return; }
      const name = this.nameEl ? this.nameEl.value.trim() : '';
      const email = this.emailEl ? this.emailEl.value.trim() : '';
      if (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        e.preventDefault();
        this.setFeedback('Please enter your name and a valid email.', false);
        return;
      }
      this.syncBooking();

      if (this.endpoint) {
        e.preventDefault();
        this.submitEl.disabled = true;
        const prev = this.submitEl.textContent;
        this.submitEl.textContent = 'Sending…';
        const hasSlot = !!this.selected;
        try {
          const resp = await fetch(this.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ start: hasSlot ? this.selected.start.toISOString() : null, end: hasSlot ? this.selected.end.toISOString() : null, flexible: !hasSlot, name, email, phone: this.phoneEl ? this.phoneEl.value.trim() : '', note: this.noteEl ? this.noteEl.value.trim() : '', topic: this.topicEl ? this.topicEl.value.trim() : '', tz: this.tz }) });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || data.ok === false) throw new Error(data.error || resp.statusText);
          if (this.slotPanel) this.slotPanel.innerHTML = '';
          if (this.formBody) this.formBody.hidden = true;
          this.listEl.innerHTML = `<p class="studio-cal__status">${this.successMsg}</p>`;
          this.announce(this.successMsg);
        } catch (err) {
          this.setFeedback(`Could not send: ${err?.message || 'error'}. Please try again.`, false);
          this.submitEl.disabled = false;
          this.submitEl.textContent = prev;
        }
        return;
      }

      // Echtes Shopify-Kontaktformular: nativ absenden (Captcha + /contact-Route kommen von {% form %})
      this.submitEl.textContent = 'Sending…';
    }

    announce(msg) { if (this.liveEl) this.liveEl.textContent = msg; }
    setFeedback(msg, ok) {
      if (!this.feedbackEl) return;
      this.feedbackEl.textContent = msg;
      this.feedbackEl.className = 'studio-cal__feedback' + (ok ? ' is-ok' : ' is-error');
    }
  }

  customElements.define('studio-calendar', StudioCalendar);
})();
